/**
 * myPay — the ledger behind the My Pay page (Oct 19, 2026).
 *
 * Greg: "a way for them to track their own pay … a money tab or pay tab for
 * them to review. keep track." Every number here is money that was PAID and
 * stored: commission_payments (each payout line, since the Sep 25, 2026
 * ledger) and spiffs.paid_at (every SPIFF). Grouped by America/New_York day,
 * month, quarter and calendar year, with the same filters the emailed
 * statements use (commission-paid-email: a live line of at least half a cent,
 * a SPIFF of more than $0), so the page and the statements always agree.
 * What is still coming (pending / approved, projections) comes from the
 * business snapshot instead, never from here.
 *
 * Reads are always filtered to ONE person: a TL / ASM can read their team's
 * commission rows under RLS, and finance everyone's.
 */
import { supabase } from "./supabase";
import { etDay, todayET } from "./businessSnapshot";

/** The day the CRM began storing each commission payout; earlier commissions are not itemized. */
export const LEDGER_START = "2026-09-25";

export type PeriodKind = "month" | "quarter" | "year";
export type StatementKind = "commission" | "spiff" | PeriodKind;

export interface PayEvent { id: string; day: string; kind: "commission" | "spiff"; amount: number }

export interface PayDay { day: string; commission: number; spiff: number; total: number; lines: number; spiffs: number }

export interface PeriodTotal {
  kind: PeriodKind;
  start: string;
  end: string;
  label: string;
  commission: number;
  spiff: number;
  total: number;
  count: number;
  /** The period ends after today: its numbers (and statement) are "so far" — the same rule the statement uses. */
  running: boolean;
}

export interface SentStatement { id: string; kind: StatementKind; day: string; status: string; mode: string | null; sentAt: string | null }

const r2 = (n: number) => Math.round(n * 100) / 100;
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-16" → "Oct 16" (date-only: never through new Date, no timezone slip). */
export function dayLabel(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : iso;
}

export function periodLabel(kind: PeriodKind, start: string): string {
  const y = start.slice(0, 4), m = Number(start.slice(5, 7));
  if (kind === "month") return `${MONTHS_FULL[m - 1]} ${y}`;
  if (kind === "quarter") return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
  return y;
}

export function periodEndOf(kind: PeriodKind, start: string): string {
  const y = Number(start.slice(0, 4)), m = Number(start.slice(5, 7));
  const months = kind === "month" ? 1 : kind === "quarter" ? 3 : 12;
  return new Date(Date.UTC(y, m - 1 + months, 0)).toISOString().slice(0, 10);
}

// PostgREST answers at most 1,000 rows a request; `build` returns a fresh, ordered query for each page.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function pageAll<T>(build: () => any): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build().range(from, from + 999);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as T[];
    out.push(...rows);
    if (rows.length < 1000) return out;
    if (from >= 50_000) throw new Error("more pay rows than expected");
  }
}

/** Every payout to one person: live commission lines and paid SPIFFs, newest day first. */
export async function fetchPayEvents(memberId: string): Promise<PayEvent[]> {
  const [lines, spiffs] = await Promise.all([
    pageAll<{ id: string; amount: number | string; paid_at: string }>(() =>
      supabase.from("commission_payments").select("id, amount, paid_at")
        .eq("member_id", memberId).is("voided_at", null).order("id")),
    pageAll<{ id: string; amount_cents: number | string; paid_at: string | null }>(() =>
      supabase.from("spiffs").select("id, amount_cents, paid_at")
        .eq("user_id", memberId).not("paid_at", "is", null).order("id")),
  ]);
  const out: PayEvent[] = [];
  for (const r of lines) {
    const amount = Number(r.amount);
    if (Number.isFinite(amount) && amount >= 0.005) out.push({ id: r.id, day: etDay(r.paid_at), kind: "commission", amount });
  }
  for (const r of spiffs) {
    const cents = Number(r.amount_cents) || 0;
    if (r.paid_at && cents > 0) out.push({ id: r.id, day: etDay(r.paid_at), kind: "spiff", amount: Math.round(cents) / 100 });
  }
  return out.sort((a, b) => b.day.localeCompare(a.day));
}

/** One row per pay day, newest first. */
export function payDays(events: PayEvent[]): PayDay[] {
  const by = new Map<string, PayDay>();
  for (const e of events) {
    const d = by.get(e.day) ?? { day: e.day, commission: 0, spiff: 0, total: 0, lines: 0, spiffs: 0 };
    if (e.kind === "commission") { d.commission += e.amount; d.lines += 1; } else { d.spiff += e.amount; d.spiffs += 1; }
    d.total += e.amount;
    by.set(e.day, d);
  }
  return [...by.values()]
    .map((d) => ({ ...d, commission: r2(d.commission), spiff: r2(d.spiff), total: r2(d.total) }))
    .sort((a, b) => b.day.localeCompare(a.day));
}

function totalIn(events: PayEvent[], start: string, end: string): { commission: number; spiff: number; count: number } {
  let commission = 0, spiff = 0, count = 0;
  for (const e of events) {
    if (e.day < start || e.day > end) continue;
    if (e.kind === "commission") commission += e.amount; else spiff += e.amount;
    count += 1;
  }
  return { commission: r2(commission), spiff: r2(spiff), count };
}

/**
 * The year's statements, newest first: every month that has started, every quarter
 * that has started, and the year itself. Each with what was paid in it.
 */
export function periodTotals(events: PayEvent[], year: number, today = todayET()): PeriodTotal[] {
  const out: PeriodTotal[] = [];
  const add = (kind: PeriodKind, start: string) => {
    if (start > today) return;
    const end = periodEndOf(kind, start);
    const t = totalIn(events, start, end);
    out.push({ kind, start, end, label: periodLabel(kind, start), ...t, total: r2(t.commission + t.spiff), running: end > today });
  };
  for (let m = 12; m >= 1; m--) add("month", `${year}-${String(m).padStart(2, "0")}-01`);
  for (const m of [10, 7, 4, 1]) add("quarter", `${year}-${String(m).padStart(2, "0")}-01`);
  add("year", `${year}-01-01`);
  return out;
}

/** Calendar years with any pay, plus the current one, newest first. */
export function payYears(events: PayEvent[], today = todayET()): number[] {
  const years = new Set<number>([Number(today.slice(0, 4))]);
  for (const e of events) years.add(Number(e.day.slice(0, 4)));
  return [...years].sort((a, b) => b - a);
}

/** The statements emailed to this person (the queue rows they can read), keyed "kind:day". */
export async function fetchSentStatements(memberId: string): Promise<Map<string, SentStatement>> {
  const rows = await pageAll<{ id: string; kind: string; pay_day: string; period_start: string | null; status: string; mode: string | null; sent_at: string | null }>(() =>
    supabase.from("commission_payment_emails")
      .select("id, kind, pay_day, period_start, status, mode, sent_at")
      .eq("member_id", memberId).order("id"));
  const map = new Map<string, SentStatement>();
  for (const r of rows) {
    const kind = r.kind as StatementKind;
    const day = r.period_start ?? r.pay_day;
    const key = `${kind}:${day}`;
    const prev = map.get(key);
    // keep the row that actually went out; otherwise the newest
    if (!prev || (r.status === "sent" && prev.status !== "sent")) {
      map.set(key, { id: r.id, kind, day, status: r.status, mode: r.mode, sentAt: r.sent_at });
    }
  }
  return map;
}

/** The body for a dry render of one statement (commission-paid-email; a person may render their own). */
export function previewBody(memberId: string, kind: StatementKind, day: string): Record<string, unknown> {
  const preview = kind === "commission" || kind === "spiff"
    ? { member_id: memberId, kind, pay_day: day }
    : { member_id: memberId, kind, period_start: day };
  return { preview, dry: true };
}
